import React from "react";
import { PublicLayout, Eyebrow, SectionCTA, IMG, ClientServicesPreview, ReferralPreview } from "@/components/public/PublicKit";
import { Users2, ClipboardCheck, Layers, Share2, Eye, CheckCircle2 } from "lucide-react";

const BLOCKS = [
  {
    icon: Users2, title: "Provider CRM",
    desc: "A shared pipeline for the relationships that drive your business.",
    points: ["Leads, practices and physician contacts", "Account owners and collaboration", "Opportunities, tasks and follow-ups", "Activity timeline on every record"],
  },
  {
    icon: ClipboardCheck, title: "Client onboarding",
    desc: "Turn a won deal into an active, producing client.",
    points: ["Clearly labeled client status", "Documentation & DTCM form completion", "Readiness-to-start tracking", "Onboarding separate from the sales stage"],
  },
  {
    icon: Layers, title: "Multiple services",
    desc: "A client can participate in several services simultaneously.",
    points: ["340B, RPM and infusion out of the box", "Admins add their own services", "Per-service status, partner, start date and owner", "Each service progresses independently"],
  },
  {
    icon: Share2, title: "Referral tracking",
    desc: "Follow referrals from submission through completion.",
    points: ["Referring client, service and receiving partner", "Owner, referral date and internal reference", "Submitted → Contacted → Accepted → In Progress → Completed", "Status history recorded on the client timeline"],
  },
  {
    icon: Eye, title: "Team visibility",
    desc: "Everyone sees who owns what and what happens next.",
    points: ["Responsible owners on clients and referrals", "Recent activity at a glance", "Next follow-up dates and overdue flags", "Dashboard counts across services and referrals"],
  },
];

export default function Platform() {
  return (
    <PublicLayout>
      <section className="border-b border-slate-100 bg-slate-50">
        <div className="mx-auto max-w-7xl px-5 py-16 lg:px-8 lg:py-20">
          <Eyebrow>The Platform</Eyebrow>
          <h1 className="max-w-3xl font-display text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl">One workspace for provider relationships and service activation</h1>
          <p className="mt-4 max-w-2xl text-lg text-slate-500">Everything your team needs to acquire providers, onboard clients, activate services, and track referrals — without juggling spreadsheets.</p>
        </div>
      </section>

      {BLOCKS.map((b, i) => (
        <section key={b.title} className={i % 2 ? "bg-slate-50" : "bg-white"}>
          <div className={`mx-auto grid max-w-7xl items-center gap-10 px-5 py-14 lg:grid-cols-2 lg:px-8 ${i % 2 ? "" : ""}`}>
            <div className={i % 2 ? "lg:order-2" : ""}>
              <div className="mb-4 inline-flex rounded-xl bg-primary/10 p-3 text-primary"><b.icon className="h-6 w-6" /></div>
              <h2 className="font-display text-3xl font-bold text-slate-900">{b.title}</h2>
              <p className="mt-2 text-lg text-slate-500">{b.desc}</p>
              <ul className="mt-5 space-y-2.5">
                {b.points.map((p) => (
                  <li key={p} className="flex items-start gap-2.5 text-slate-600"><CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-primary" /><span>{p}</span></li>
                ))}
              </ul>
            </div>
            <div className={i % 2 ? "lg:order-1" : ""}>
              {b.title === "Referral tracking" ? <ReferralPreview />
                : b.title === "Multiple services" ? <ClientServicesPreview />
                : <img src={i === 0 ? IMG.team : i === 1 ? IMG.data : IMG.pharmacy} alt={b.title} loading="lazy" className="aspect-[4/3] w-full rounded-3xl object-cover shadow-lg ring-1 ring-slate-200" />}
            </div>
          </div>
        </section>
      ))}

      <SectionCTA />
    </PublicLayout>
  );
}
